// WHIPT SUMM! - Academic PDF Analysis Tool
// Upload PDF → Get Scholar's Quick Reference + Study Tools
import React, { useState, useRef } from 'react';
import { Upload, Loader2, FileText, Sparkles, BookOpen, Brain, ClipboardCheck, X } from 'lucide-react';
import { extractDocumentText } from '@/lib/documentExtractor';
import { generateSummary } from '@/lib/summaryGenerator';
import { useAuth } from '@/contexts/AuthContext';

interface FlashCard {
  id: number;
  front: string;
  back: string;
  category: string;
}

interface TestQuestion {
  id: number;
  question: string;
  options?: string[];
  answer: string;
  type: 'multiple-choice' | 'short-answer';
}

const WhiptSummPage: React.FC = () => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [extractedText, setExtractedText] = useState('');
  const [summary, setSummary] = useState('');
  const [flashCards, setFlashCards] = useState<FlashCard[]>([]);
  const [testQuestions, setTestQuestions] = useState<TestQuestion[]>([]);
  const [notes, setNotes] = useState('');
  const [activeView, setActiveView] = useState<'summary' | 'flashcards' | 'questions' | 'notes'>('summary');
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      handleProcessPDF(file);
    } else {
      alert('Please select a PDF file');
    }
  };

  // Process PDF
  const handleProcessPDF = async (file: File) => {
    setIsProcessing(true);
    setProcessingStep('Extracting text from PDF...');
    
    try {
      // Extract text
      const extracted = await extractDocumentText(file);
      setExtractedText(extracted.text);
      
      // Generate summary
      setProcessingStep('Generating Scholar\'s Quick Reference...');
      const generatedSummary = await generateSummary(extracted.text, file.name);
      setSummary(generatedSummary);
      
      // Generate flash cards
      setProcessingStep('Creating flash cards...');
      await generateFlashCards(extracted.text);
      
      // Generate test questions
      setProcessingStep('Generating test questions...');
      await generateTestQuestions(extracted.text);
      
      setProcessingStep('');
      alert('✅ PDF analyzed successfully!');
    } catch (err: any) {
      console.error('PDF processing failed:', err);
      alert(`Failed to process PDF: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Generate flash cards from text
  const generateFlashCards = async (text: string) => {
    // TODO: Call AI to generate flash cards
    // For now, create placeholder cards
    const sampleCards: FlashCard[] = [
      { id: 1, front: 'What is the research methodology?', back: 'Quantitative study with N=150 participants', category: 'Methodology' },
      { id: 2, front: 'Main finding?', back: 'Significant positive correlation (p<0.05)', category: 'Findings' },
      { id: 3, front: 'Author\'s conclusion?', back: 'Intervention shows promise for future application', category: 'Conclusions' },
    ];
    setFlashCards(sampleCards);
  };

  // Generate test questions
  const generateTestQuestions = async (text: string) => {
    // TODO: Call AI to generate test questions
    // For now, create placeholder questions
    const sampleQuestions: TestQuestion[] = [
      {
        id: 1,
        question: 'What was the sample size of this study?',
        options: ['N=50', 'N=100', 'N=150', 'N=200'],
        answer: 'N=150',
        type: 'multiple-choice'
      },
      {
        id: 2,
        question: 'Describe the main limitation of this research.',
        answer: 'Small sample size limited generalizability',
        type: 'short-answer'
      },
    ];
    setTestQuestions(sampleQuestions);
  };

  // Flip flash card
  const toggleCardFlip = (cardId: number) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            WHIPT SUMM<span className="text-amber-400">!</span>
          </h1>
          <p className="text-xl text-slate-300">
            Academic PDF Analysis • Scholar's Quick Reference • Study Tools
          </p>
        </div>

        {/* Upload Section */}
        {!pdfFile && (
          <div className="max-w-2xl mx-auto">
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-4 border-dashed border-purple-400/50 rounded-3xl p-16 text-center hover:border-purple-400 hover:bg-purple-500/10 transition-all cursor-pointer"
            >
              <Upload className="w-20 h-20 text-purple-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Upload Your PDF</h2>
              <p className="text-slate-300 mb-4">
                Scholarly articles, research papers, dissertations
              </p>
              <button className="px-8 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-lg hover:scale-105 transition-transform">
                Choose File
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {/* Processing Loader */}
        {isProcessing && (
          <div className="max-w-2xl mx-auto bg-black/40 rounded-2xl p-8 border border-purple-400/30">
            <div className="flex items-center justify-center gap-4 mb-4">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
              <span className="text-xl text-white font-semibold">{processingStep}</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 animate-pulse"></div>
            </div>
          </div>
        )}

        {/* Results - After Processing */}
        {pdfFile && !isProcessing && summary && (
          <div className="space-y-6">
            
            {/* File Info */}
            <div className="bg-black/40 rounded-2xl p-6 border border-purple-400/30 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <FileText className="w-12 h-12 text-purple-400" />
                <div>
                  <h3 className="text-xl font-bold text-white">{pdfFile.name}</h3>
                  <p className="text-slate-400">Analyzed and ready for study</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setPdfFile(null);
                  setSummary('');
                  setFlashCards([]);
                  setTestQuestions([]);
                  setNotes('');
                }}
                className="p-3 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation Tabs */}
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setActiveView('summary')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
                  activeView === 'summary'
                    ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg shadow-amber-500/40'
                    : 'bg-black/40 text-slate-300 border border-purple-400/30 hover:bg-purple-500/20'
                }`}
              >
                <Sparkles className="w-5 h-5" />
                Scholar's Quick Reference
              </button>
              
              <button
                onClick={() => setActiveView('flashcards')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
                  activeView === 'flashcards'
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/40'
                    : 'bg-black/40 text-slate-300 border border-purple-400/30 hover:bg-purple-500/20'
                }`}
              >
                <BookOpen className="w-5 h-5" />
                Flash Cards ({flashCards.length})
              </button>
              
              <button
                onClick={() => setActiveView('questions')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
                  activeView === 'questions'
                    ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg shadow-emerald-500/40'
                    : 'bg-black/40 text-slate-300 border border-purple-400/30 hover:bg-purple-500/20'
                }`}
              >
                <Brain className="w-5 h-5" />
                Test Questions ({testQuestions.length})
              </button>
              
              <button
                onClick={() => setActiveView('notes')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
                  activeView === 'notes'
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/40'
                    : 'bg-black/40 text-slate-300 border border-purple-400/30 hover:bg-purple-500/20'
                }`}
              >
                <ClipboardCheck className="w-5 h-5" />
                My Notes
              </button>
            </div>

            {/* Content Views */}
            <div className="bg-black/40 rounded-2xl p-8 border border-purple-400/30">
              
              {/* Summary View */}
              {activeView === 'summary' && (
                <div className="prose prose-invert prose-amber max-w-none">
                  <div className="whitespace-pre-wrap text-amber-50 leading-relaxed">
                    {summary}
                  </div>
                </div>
              )}

              {/* Flash Cards View */}
              {activeView === 'flashcards' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {flashCards.map(card => (
                    <div
                      key={card.id}
                      onClick={() => toggleCardFlip(card.id)}
                      className="relative h-64 cursor-pointer perspective-1000"
                    >
                      <div className={`relative w-full h-full transition-transform duration-500 transform-style-3d ${
                        flippedCards.has(card.id) ? 'rotate-y-180' : ''
                      }`}>
                        {/* Front */}
                        <div className="absolute inset-0 backface-hidden bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl p-6 flex flex-col justify-between shadow-xl">
                          <span className="text-xs font-bold text-cyan-200 uppercase">{card.category}</span>
                          <p className="text-xl font-bold text-white text-center">{card.front}</p>
                          <span className="text-xs text-cyan-200 text-center">Click to reveal</span>
                        </div>
                        {/* Back */}
                        <div className="absolute inset-0 backface-hidden bg-gradient-to-br from-emerald-600 to-green-700 rounded-2xl p-6 flex items-center justify-center shadow-xl rotate-y-180">
                          <p className="text-lg text-white text-center">{card.back}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Test Questions View */}
              {activeView === 'questions' && (
                <div className="space-y-6">
                  {testQuestions.map((q, idx) => (
                    <div key={q.id} className="bg-slate-800/50 rounded-xl p-6 border border-emerald-400/30">
                      <h4 className="text-lg font-bold text-white mb-4">
                        {idx + 1}. {q.question}
                      </h4>
                      {q.type === 'multiple-choice' && q.options && (
                        <div className="space-y-2">
                          {q.options.map((opt, i) => (
                            <label key={i} className="flex items-center gap-3 p-3 rounded-lg bg-black/30 hover:bg-emerald-500/20 cursor-pointer transition-colors">
                              <input type="radio" name={`q-${q.id}`} className="w-5 h-5" />
                              <span className="text-slate-200">{opt}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {q.type === 'short-answer' && (
                        <textarea
                          placeholder="Type your answer here..."
                          className="w-full p-4 rounded-lg bg-black/40 border border-slate-600 text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400"
                          rows={4}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Notes View */}
              {activeView === 'notes' && (
                <div>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add your study notes, insights, and annotations here..."
                    className="w-full h-96 p-6 rounded-xl bg-black/40 border border-purple-400/30 text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-400 text-lg leading-relaxed"
                  />
                </div>
              )}
              
            </div>
          </div>
        )}
      </div>

      {/* Custom CSS for 3D flip effect */}
      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </div>
  );
};

export default WhiptSummPage;
